import { Component } from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';

import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    share,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';

import {
    ActionItem,
    ActionItemIconPosition,
    ActionItemStyle,
    AutocompleteFunction,
    BaseNestedFieldDefinition,
    BatchFieldReadHelper,
    CustomErrorType,
    DialogRef,
    DialogStyle,
    ExtendedBaseProfileField,
    FieldReadHelper,
    FieldType,
    FormSelectOption,
    FormValue,
    FormValueRequired,
    FormValueState,
    isRequiredControl,
    MHLocation,
    MultiFormSelectOptions,
    multiFormSelectOptionWrapper,
    PartialRecord,
    ShComplexInputFieldComponent,
    ShDynamicDialogService,
    ShGeoService,
    ShGoogleMapsDialogComponent,
    TypedValue
} from '@stylehaven/components';
import { isEmpty, isEqual } from 'lodash-es';

@Component({
    selector: 'med-address-field',
    templateUrl: './address-input-field.component.html'
})
export class AddressInputFieldComponent extends ShComplexInputFieldComponent {
    public addressSearchControl: UntypedFormControl;
    public geoLocationSearchControl: UntypedFormControl;
    public complexAddressControl: UntypedFormControl;

    public geoCoordinateFieldDef: BaseNestedFieldDefinition;

    // Time-outs in ms to prevent too many requests
    public readonly ADDRESS_INPUT_DEBOUNCE_TIME: number = 1000;
    public readonly GEO_INPUT_DEBOUNCE_TIME: number = 2000;
    private readonly validCoordinateExpr: RegExp =
        /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)\s*,\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

    public addressSuggestions$: BehaviorSubject<MultiFormSelectOptions<MHLocation>> = new BehaviorSubject<
        MultiFormSelectOptions<MHLocation>
    >({ isLiveData: false, options: [] });
    public geoLocationSuggestions$: BehaviorSubject<MultiFormSelectOptions<MHLocation>> = new BehaviorSubject(
        multiFormSelectOptionWrapper([])
    );
    public addressErrorMsg$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public geoLocationErrorMsg$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    private geoLocationSearch$: Subject<string> = new Subject<string>();

    public openGoogleMapsActionItem: ActionItem;
    public convertToCoordinatesActionItem$: Observable<ActionItem>;
    public convertToAddressActionItem$: Observable<ActionItem>;

    public addressAutoCompleteFct: AutocompleteFunction;
    public coordinateAutoCompleteFct: AutocompleteFunction;

    private addressLocation$: BehaviorSubject<MHLocation | null> = new BehaviorSubject<MHLocation | null>(null);
    private coordinateLocation$: BehaviorSubject<MHLocation | null> = new BehaviorSubject<MHLocation | null>(null);
    private locationSynced$: Observable<boolean>;

    // This is to disable listeners when setValue is used because emitEvent false incorrectly triggers valueChanges sometimes!
    private allowSearchControlListeners$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    constructor(
        protected fieldHelper: FieldReadHelper,
        private geoService: ShGeoService,
        private dynamicDialogService: ShDynamicDialogService
    ) {
        super(fieldHelper);
    }

    private coordinateValidatorFn: ValidatorFn = control => {
        if (!control.value) {
            return null;
        }

        let controlValue: string;
        if (control.value instanceof FormValue) {
            if (!control.value.value) {
                return null;
            }

            controlValue = control.value.value;
        } else {
            controlValue = control.value;
        }

        return controlValue.match(this.validCoordinateExpr) ? null : { invalidFormat: control.value.value };
    };

    openGoogleMaps(): void {
        const dialog: DialogRef<string> = this.openShGoogleMapsDialogComponent();
        this.setLocationOnShGoogleMapsDialogComponentClose(dialog);
    }

    init(profileField: ExtendedBaseProfileField, fragments: PartialRecord[], control: UntypedFormControl): void {
        this.complexAddressControl = control;
        this.initAddressSearchControl(fragments, profileField);
        this.initGeoLocationControl(profileField);

        this.initLocationSyncedStream();

        this.initAddressSearchListener();
        this.initGeoLocationListeners();

        this.initGoogleMapsActionItem();
        this.initOverwriteCoordinatesActionitem();
        this.initOverwriteAddressActionItem();
    }

    // #region Address control
    private initAddressSearchControl(fragments: PartialRecord[], profileField: ExtendedBaseProfileField): void {
        this.addressSearchControl = new UntypedFormControl();
        if (isRequiredControl(this.complexAddressControl)) {
            this.addressSearchControl.setValidators(FormValueRequired);
        }

        const addressValues: FormValue<TypedValue>[] = this.isBatchEdit
            ? (BatchFieldReadHelper.getBatchFieldValue(profileField.FieldDefinition) as FormValue<TypedValue>[])
            : (this.fieldHelper.getMergedMapFieldValue(
                  profileField.FieldDefinition,
                  fragments
              ) as FormValue<TypedValue>[]);

        this.setAddressSearchValue(addressValues);
        this.complexAddressControl.setValue(addressValues);

        if (this.complexAddressControl.disabled) {
            this.addressSearchControl.disable();
        }
    }

    private setAddressSearchValue(complexAddress: FormValue<TypedValue>[]): void {
        const formattedAddressTypedValue: FormValue<TypedValue> | undefined = complexAddress.find(
            formValue => formValue.value.type === 'Address'
        );

        if (!formattedAddressTypedValue || formattedAddressTypedValue.state === FormValueState.UNSET) {
            this.addressSearchControl.setValue(new FormValue('', FormValueState.UNSET), { emitEvent: false });
            return;
        }

        this.addressSearchControl.setValue(
            new FormValue(formattedAddressTypedValue.value.value, formattedAddressTypedValue.state),
            { emitEvent: false }
        );
    }

    private initAddressSearchListener(): void {
        this.addressAutoCompleteFct = (term: string) => {
            return term
                ? this.findAddress(term).pipe(
                      map(this.suggestionToMultiSelectOptions),
                      tap(suggestions => {
                          this.addressSuggestions$.next(suggestions);
                      }),
                      map(suggestions => ({
                          isLiveData: false,
                          options: suggestions.options.map(suggestion => ({
                              ...suggestion,
                              value: suggestion.value!.Address
                          }))
                      }))
                  )
                : of(multiFormSelectOptionWrapper([]));
        };

        const changesWithDebounce$: Observable<string | FormValue<string>> =
            this.addressSearchControl.valueChanges.pipe(
                filter(_ => this.allowSearchControlListeners$.value === true),
                distinctUntilChanged(isEqual),
                share()
            );

        combineLatest([changesWithDebounce$, this.addressSuggestions$.pipe(distinctUntilChanged())])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([formValue, suggestions]) => {
                const value: string | null = formValue instanceof FormValue ? formValue.value : formValue;
                const hasValue = value !== null;

                if (hasValue && (!suggestions || suggestions.options.length === 0)) {
                    if (formValue instanceof FormValue && formValue.value?.trim().length > 0) {
                        this.addressErrorMsg$.next(this.translateService.instant('form.location.invalid-location'));
                    }
                    return;
                }

                if (value === null) {
                    this.complexAddressControl.setValue(this.locationToFormValues(this.coordinateValue, null));
                } else {
                    const searchTerm: FormValue = this.addressValue;

                    // Look for an exact match in the suggestions, otherwise pick the first one
                    const index: number = suggestions.options.findIndex(
                        suggestion => suggestion.label === searchTerm.value
                    );
                    if (index >= 0) {
                        this.addressLocation$.next(suggestions.options[index].value);
                    } else {
                        this.addressLocation$.next(suggestions.options[0].value);
                    }
                    this.complexAddressControl.setValue(
                        this.locationToFormValues(this.coordinateValue, this.addressValue)
                    );
                }
                this.addressErrorMsg$.next(null);
            });
    }

    private findAddress(searchTerm: string): Observable<MHLocation | null> {
        if (!searchTerm || searchTerm === '') {
            return of(null);
        }

        return this.geoService.searchByAddress(searchTerm);
    }
    // #endregion

    // #region Geolocation control
    private initGeoLocationControl(positionField: ExtendedBaseProfileField): void {
        const mergedGeoLocation: FormValue<TypedValue> | undefined = this.complexAddressControl.value.find(
            (value: FormValue<TypedValue>) => value?.value?.type === 'Location'
        );

        const locationField: BaseNestedFieldDefinition | undefined = positionField.FieldDefinition.Children?.find(
            def => def.Type === FieldType.GeoCoordinateField
        );
        if (!locationField) {
            console.error('AddressInputFieldComponent: no GeoCoordinateField found');
        } else {
            this.geoCoordinateFieldDef = locationField;
        }

        this.geoLocationSearchControl = new UntypedFormControl();
        this.geoLocationSearchControl.addValidators(this.coordinateValidatorFn);

        if (!mergedGeoLocation || mergedGeoLocation.state === FormValueState.UNSET) {
            this.geoLocationSearchControl.setValue(new FormValue(null, FormValueState.UNSET), { emitEvent: false });
            return;
        }

        if (isRequiredControl(this.complexAddressControl)) {
            this.geoLocationSearchControl.setValidators(FormValueRequired);
        }

        this.geoLocationSearchControl.setValue(new FormValue(mergedGeoLocation?.value.value, mergedGeoLocation?.state));

        if (this.complexAddressControl.disabled) {
            this.geoLocationSearchControl.disable();
        }
    }

    private initGeoLocationListeners(): void {
        this.coordinateAutoCompleteFct = (term: string) => {
            return term && this.geoLocationSearchControl.valid
                ? this.findAddress(term).pipe(
                      tap(() => this.geoLocationErrorMsg$.next(null)),
                      map(this.suggestionToMultiSelectOptions),
                      tap(suggestions => {
                          this.geoLocationSuggestions$.next(suggestions);
                      }),
                      map(suggestions => ({
                          isLiveData: false,
                          options: suggestions.options.map(suggestion => ({
                              ...suggestion,
                              value: suggestion.value!.Location
                          }))
                      }))
                  )
                : of(multiFormSelectOptionWrapper([]));
        };

        const changesWithDebounce$: Observable<string | FormValue<string>> =
            this.geoLocationSearchControl.valueChanges.pipe(distinctUntilChanged(isEqual), share());

        combineLatest([
            changesWithDebounce$.pipe(filter(_ => this.allowSearchControlListeners$.value === true)),
            this.geoLocationSuggestions$.pipe(distinctUntilChanged())
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([formValue, suggestions]) => {
                const value: string | null = formValue instanceof FormValue ? formValue.value : formValue;
                const hasValue = value !== null;

                if (hasValue && (!suggestions || suggestions.options.length === 0)) {
                    if (formValue instanceof FormValue && formValue.value?.trim().length > 0) {
                        this.geoLocationErrorMsg$.next(
                            this.translateService.translate('form.location.invalid-location')
                        );
                    }
                    return;
                }

                if (value === null) {
                    this.complexAddressControl.setValue(this.locationToFormValues(null, this.addressValue));
                } else {
                    const searchTerm: FormValue = this.coordinateValue;

                    // Look for an exact match in the suggestions, otherwise pick the first one
                    const index: number = suggestions.options.findIndex(
                        suggestion => suggestion.label === searchTerm.value
                    );
                    if (index >= 0) {
                        this.coordinateLocation$.next(suggestions.options[index].value);
                    } else {
                        this.coordinateLocation$.next(suggestions.options[0].value);
                    }

                    this.complexAddressControl.setValue(
                        this.locationToFormValues(this.coordinateValue, this.addressValue)
                    );
                }
            });
    }

    private suggestionToMultiSelectOptions(addressSuggestion: MHLocation | null): MultiFormSelectOptions<MHLocation> {
        const formattedAddress: string | undefined = addressSuggestion?.Address;
        const options: FormSelectOption<MHLocation>[] = [];

        if (addressSuggestion && formattedAddress) {
            options.push({
                label: formattedAddress,
                value: addressSuggestion
            });
        }

        return {
            options,
            isLiveData: true
        };
    }

    private findGeoLocation(geoLocation: string): Observable<MHLocation | null> {
        if (!geoLocation || geoLocation === '') {
            return of(null);
        }

        return this.geoService.searchByGeoLocation(geoLocation).pipe(
            map(suggestion => this.retainGeoLocationInput(suggestion, geoLocation)),
            catchError(error => {
                if (error === CustomErrorType.InvalidLatLngFormat) {
                    this.geoLocationErrorMsg$.next(this.translateService.instant('form.location.invalid-format'));
                } else {
                    this.geoLocationErrorMsg$.next(this.translateService.instant('form.location.invalid-location'));
                }
                return of(null);
            })
        );
    }

    /**
     * We want to retain the exact geocoordinate input of the user
     * Since Google Maps returns the exact geolocation of the suggested address
     */
    private retainGeoLocationInput(suggestion: MHLocation | null, input: string): MHLocation | null {
        if (!suggestion) {
            return null;
        }

        const formattedInput: string = this.formatGeolocationValue(input);

        return {
            ...suggestion,
            Location: formattedInput
        };
    }

    onGeoLocationSearchTermChanged(searchTerm: string): void {
        this.geoLocationSearch$.next(searchTerm);
    }
    // #endregion

    // #region LocationSync
    private initLocationSyncedStream(): void {
        this.locationSynced$ = combineLatest([this.addressLocation$, this.coordinateLocation$]).pipe(
            map(([address, geolocation]) => isEqual(address, geolocation)),
            shareReplay()
        );
    }
    // #endregion

    // #region Google Maps
    private initGoogleMapsActionItem(): void {
        this.openGoogleMapsActionItem = {
            label: this.translateService.instant('modules.general.form.location.search-map-location'),
            style: ActionItemStyle.Link,
            icon: 'search',
            iconPosition: ActionItemIconPosition.Right,
            eventListener: () => this.openGoogleMaps(),
            disabled: this.complexAddressControl?.disabled,
            disabledInfoMsg: this.disabledMessageKey ? this.translateService.instant(this.disabledMessageKey) : ''
        };
    }

    private openShGoogleMapsDialogComponent(): DialogRef<string> {
        const coordinatesAsString: string =
            this.geoLocationSearchControl.value instanceof FormValue &&
            this.geoLocationSearchControl.value.state === FormValueState.SET
                ? (this.geoLocationSearchControl.value.value ?? null)
                : null;
        const dialog: DialogRef<string> = this.dynamicDialogService.open<string>(ShGoogleMapsDialogComponent, {
            title: this.translateService.instant('modules.general.form.location.search-map-location'),
            options: {
                style: DialogStyle.ExtraWide
            },
            data: coordinatesAsString?.replace(/ /g, '')
        });
        return dialog;
    }

    private setLocationOnShGoogleMapsDialogComponentClose(dialog: DialogRef<string>): void {
        dialog.afterClosed$
            .pipe(
                take(1),
                takeUntil(this.destroy$),
                filter((coordinateString): coordinateString is string => !!coordinateString),
                switchMap(coordinate => this.findGeoLocation(coordinate))
            )
            .subscribe(mh2Location => {
                if (mh2Location) {
                    this.setCombinedLocationValue(mh2Location);
                }
            });
    }
    // #endregion

    // #region Overwrite Action Items
    private initOverwriteCoordinatesActionitem(): void {
        const controlValueDefined$: Observable<boolean> = this.addressSearchControl.valueChanges.pipe(
            startWith(this.addressSearchControl.value),
            map(formValue => !!formValue?.value && formValue.state === FormValueState.SET)
        );

        this.convertToCoordinatesActionItem$ = combineLatest([
            controlValueDefined$,
            this.addressErrorMsg$,
            this.locationSynced$
        ]).pipe(
            map(([isDefined, error, isSynced]) => {
                let disabledInfoMsg: string = '';
                if (error) {
                    disabledInfoMsg = error;
                } else if (this.disabledMessageKey) {
                    disabledInfoMsg = this.translateService.instant(this.disabledMessageKey);
                }

                return {
                    label: this.translateService.instant('modules.general.form.location.overwrite-coordinates'),
                    style: ActionItemStyle.Link,
                    icon: 'arrow-down',
                    iconPosition: ActionItemIconPosition.Right,
                    eventListener: () => this.convertToCoordinates(),
                    disabled: isSynced || this.complexAddressControl?.disabled || !isDefined || !!error,
                    disabledInfoMsg
                };
            })
        );
    }

    private convertToCoordinates(): void {
        let addressLocation$: Observable<MHLocation | null>;
        if (!this.addressLocation$.value) {
            addressLocation$ = this.findAddress(this.addressSearchControl.value.value).pipe(
                tap(location => {
                    if (!location) {
                        throw new Error();
                    }
                }),
                catchError(() => {
                    this.addressErrorMsg$.next(this.translateService.instant('form.location.invalid-location'));
                    return EMPTY;
                })
            );
        } else {
            addressLocation$ = this.addressLocation$.asObservable();
        }

        addressLocation$.pipe(take(1), takeUntil(this.destroy$)).subscribe(location => {
            this.setCombinedLocationValue(location);
        });
    }

    private initOverwriteAddressActionItem(): void {
        const controlValueDefined$: Observable<boolean> = this.geoLocationSearchControl.valueChanges.pipe(
            startWith(this.geoLocationSearchControl.value),
            map(formValue => !!formValue?.value && formValue.state === FormValueState.SET)
        );
        this.convertToAddressActionItem$ = combineLatest([
            controlValueDefined$,
            this.geoLocationErrorMsg$,
            this.locationSynced$
        ]).pipe(
            map(([isDefined, error, isSynced]) => {
                let disabledInfoMsg: string = '';
                if (error) {
                    disabledInfoMsg = error;
                } else if (this.disabledMessageKey) {
                    disabledInfoMsg = this.translateService.instant(this.disabledMessageKey);
                }

                return {
                    label: this.translateService.instant('modules.general.form.location.overwrite-address'),
                    style: ActionItemStyle.Link,
                    icon: 'arrow-up',
                    iconPosition: ActionItemIconPosition.Right,
                    eventListener: () => this.convertToAddress(),
                    disabled:
                        isSynced ||
                        this.complexAddressControl?.disabled ||
                        !isDefined ||
                        !!error ||
                        !this.geoLocationSearchControl.valid,
                    disabledInfoMsg
                };
            })
        );
    }

    private convertToAddress(): void {
        let coordinatesLocation$: Observable<MHLocation | null>;
        if (!this.coordinateLocation$.value) {
            coordinatesLocation$ = this.findAddress(this.geoLocationSearchControl.value.value).pipe(
                tap(location => {
                    if (!location) {
                        throw new Error();
                    }
                }),
                catchError(() => {
                    this.geoLocationErrorMsg$.next(this.translateService.instant('form.location.invalid-location'));
                    return EMPTY;
                })
            );
        } else {
            coordinatesLocation$ = this.coordinateLocation$.asObservable().pipe(take(1));
        }

        coordinatesLocation$.pipe(take(1), takeUntil(this.destroy$)).subscribe(location => {
            this.setCombinedLocationValue(location);
        });
    }

    private setCombinedLocationValue(newLocation: MHLocation | null): void {
        if (!newLocation) {
            return;
        }

        this.allowSearchControlListeners$.next(false);

        const formattedInput: string = this.formatGeolocationValue(newLocation.Location);

        // Set search input values
        this.addressSearchControl.setValue(new FormValue(newLocation.Address), { emitEvent: false });
        this.geoLocationSearchControl.setValue(new FormValue(formattedInput), {
            emitEvent: false
        });

        // Sync internal values
        this.addressLocation$.next(newLocation);
        this.coordinateLocation$.next(newLocation);

        const positionFormValues = this.locationToFormValues(
            this.geoLocationSearchControl.value,
            this.addressSearchControl.value
        );

        this.complexAddressControl.setValue(positionFormValues);

        this.allowSearchControlListeners$.next(true);
    }

    private locationToFormValues(
        coordinateLocation?: FormValue | null,
        addressSearchTerm?: FormValue | null
    ): FormValue<TypedValue>[] {
        const coordinates: string | undefined = coordinateLocation
            ? this.formatGeolocationValue(coordinateLocation.value)
            : undefined;

        return [
            new FormValue({ type: 'Location', value: coordinates ?? '' }, coordinateLocation?.state),
            new FormValue({ type: 'Address', value: addressSearchTerm?.value ?? '' }, addressSearchTerm?.state)
        ];
    }

    private formatGeolocationValue(value: string): string {
        if (isEmpty(value)) {
            return value;
        }

        // MED-1771: must be without spaces for collection ingest
        return value
            .trim()
            .replace(/\s{2,}/g, ' ')
            .split(' ', 2)
            .join('');
    }

    private get addressValue(): FormValue {
        const formValue: string | FormValue<string> = this.addressSearchControl.value;
        let searchTerm: FormValue;
        if (typeof formValue === 'string') {
            searchTerm = new FormValue(formValue, FormValueState.SET);
        } else {
            searchTerm = formValue;
        }
        return searchTerm;
    }

    private get coordinateValue(): FormValue {
        const formValue: string | FormValue<string> = this.geoLocationSearchControl.value;
        let searchTerm: FormValue;

        if (typeof formValue === 'string') {
            searchTerm = new FormValue(formValue, FormValueState.SET);
        } else {
            searchTerm = formValue;
        }
        return searchTerm;
    }
}
